import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { BookOpen, Plus, Check, ChevronRight, GraduationCap, X } from "lucide-react";

export default function CourseBuilder() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [studies, setStudies] = useState([]);
  const [selected, setSelected] = useState([]);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);
  const [loadingStudies, setLoadingStudies] = useState(true);

  useEffect(() => {
    api.get("/subcourses").then(r => {
      setStudies((r.data || []).filter(s => s.generation_status === "ready"));
      setLoadingStudies(false);
    }).catch(() => setLoadingStudies(false));
  }, []);

  const toggle = (id) => {
    if (selected.includes(id)) {
      setSelected(selected.filter(s => s !== id));
    } else if (selected.length < 5) {
      setSelected([...selected, id]);
    } else {
      toast.error("You can select up to 5 studies.");
    }
  };

  const create = async (e) => {
    e.preventDefault();
    if (selected.length < 2) { toast.error("Select at least 2 studies."); return; }
    if (!title.trim()) { toast.error("Enter a course title."); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/courses", { title, description, study_ids: selected });
      toast.success("Course created!");
      nav(`/app/courses/${data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create course");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-xl bg-[#1a1a2e] border border-[#B8A4FF]/20 flex items-center justify-center">
            <GraduationCap size={18} className="text-[#B8A4FF]" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">New Course</h1>
            <p className="text-[13px] text-[#B0AEBC]">Merge 2–5 studies into a unified course with a combined flashcard deck & exam.</p>
          </div>
        </div>

        <form onSubmit={create} className="space-y-6">
          {/* Title & description */}
          <div className="card p-5 space-y-4">
            <div>
              <label className="text-[12px] font-medium text-[#B0AEBC] mb-1.5 block">Course Title *</label>
              <input
                className="input"
                value={title}
                onChange={e => setTitle(e.target.value)}
                placeholder="e.g. JEE Advanced 2026 Full Prep"
                required
                maxLength={120}
                data-testid="course-title-input"
              />
            </div>
            <div>
              <label className="text-[12px] font-medium text-[#B0AEBC] mb-1.5 block">Description (optional)</label>
              <textarea
                className="input min-h-[80px] resize-none"
                value={description}
                onChange={e => setDescription(e.target.value)}
                placeholder="What does this course cover?"
                maxLength={500}
              />
            </div>
          </div>

          {/* Study selection */}
          <div className="card p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-[15px] font-semibold text-white">Select Studies</h2>
                <p className="text-[12px] text-[#B0AEBC] mt-0.5">Choose 2–5 ready studies to merge. ({selected.length}/5 selected)</p>
              </div>
              {selected.length > 0 && (
                <button type="button" onClick={() => setSelected([])}
                  className="text-[12px] text-[#B0AEBC] hover:text-white flex items-center gap-1">
                  <X size={12} /> Clear all
                </button>
              )}
            </div>

            {loadingStudies ? (
              <div className="text-center py-8 text-[#B0AEBC] text-[13px]">Loading studies…</div>
            ) : studies.length === 0 ? (
              <div className="text-center py-8">
                <BookOpen size={32} className="text-[#4a4a5a] mx-auto mb-3" />
                <p className="text-[#B0AEBC] text-[13px]">No ready studies found. Create some studies first.</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-[400px] overflow-y-auto pr-1">
                {studies.map(s => {
                  const isSelected = selected.includes(s.id);
                  return (
                    <motion.button
                      key={s.id}
                      type="button"
                      onClick={() => toggle(s.id)}
                      whileHover={{ scale: 1.005 }}
                      whileTap={{ scale: 0.995 }}
                      data-testid={`course-study-select-${s.id}`}
                      className={`w-full text-left p-3.5 rounded-xl border transition-all ${
                        isSelected
                          ? "border-[#B8A4FF]/40 bg-[#B8A4FF]/5"
                          : "border-[#2a2a3e] hover:border-[#3a3a4e] bg-[#131316]/60"
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-5 h-5 rounded-md border flex items-center justify-center flex-shrink-0 transition-all ${
                          isSelected ? "bg-[#B8A4FF] border-[#B8A4FF]" : "border-[#3a3a4e]"
                        }`}>
                          {isSelected && <Check size={11} className="text-black" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-[13px] font-medium text-white truncate">{s.title}</p>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                              s.rarity === "legendary" ? "bg-yellow-400/10 text-yellow-400" :
                              s.rarity === "epic" ? "bg-purple-400/10 text-purple-400" :
                              s.rarity === "rare" ? "bg-blue-400/10 text-blue-400" :
                              "bg-[#2a2a3e] text-[#B0AEBC]"
                            }`}>{s.rarity || "common"}</span>
                            {s.tags?.slice(0, 2).map(t => (
                              <span key={t} className="text-[10px] text-[#6B6B7B]">{t}</span>
                            ))}
                          </div>
                        </div>
                        {isSelected && <span className="text-[10px] text-[#B8A4FF] font-medium">#{selected.indexOf(s.id) + 1}</span>}
                      </div>
                    </motion.button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Create button */}
          <button
            type="submit"
            disabled={saving || selected.length < 2 || !title.trim()}
            className="btn btn-primary w-full py-3.5 text-[15px] flex items-center justify-center gap-2"
            data-testid="course-create-btn"
          >
            {saving ? "Creating…" : <><Plus size={16} /> Create Course</>}
          </button>
        </form>
      </div>
    </div>
  );
}
